'use client'

import axios from 'axios'
import { motion, useReducedMotion } from 'framer-motion'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { SiteShell } from '@/components/site/site-shell'
import { RevealText } from '@/components/site/reveal-text'
import { siteButtonClass } from '@/components/site/magnetic-button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { cn } from '@/lib/utils'

declare global {
  interface Window {
    Calendly?: { showPopupWidget: (url: string) => void }
  }
}

const contactDetails = [
  {
    label: 'Email',
    value: 'contact@elementarycreative.com',
    href: 'mailto:contact@elementarycreative.com',
  },
  {
    label: 'Phone',
    value: '(639) 316-4005',
    href: 'tel:+16393164005',
  },
  {
    label: 'Book a call',
    value: 'Free 30-minute consultation',
    href: 'https://calendly.com/elementarycreative/30min',
    external: true,
  },
  {
    label: 'Location',
    value: 'Vancouver, BC, Canada',
  },
] as const

const fieldClass =
  'rounded-none border-border bg-transparent focus-visible:ring-accent'

const labelClass = 'text-xs tracking-[0.2em] uppercase text-muted-foreground'

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().min(1, 'Email is required').email('Please enter a valid email'),
  phone: z.string(),
  message: z.string().min(1, 'Message is required'),
  hpField: z.string().optional(),
})

type ContactFormValues = z.infer<typeof formSchema>

function ContactForm() {
  const [submitted, setSubmitted] = useState(false)
  const reduceMotion = useReducedMotion()

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '', email: '', phone: '', message: '', hpField: '' },
  })

  async function onSubmit(values: ContactFormValues) {
    if (values.hpField?.trim()) {
      setSubmitted(true)
      form.reset()
      return
    }

    try {
      await axios.post(
        import.meta.env.NEXT_PUBLIC_CONTACT_FORM_URL ||
          'https://egpmgusqyi.execute-api.us-east-1.amazonaws.com/main/contact',
        {
          name: values.name,
          email: values.email,
          telephone: values.phone,
          message: values.message,
        }
      )
      setSubmitted(true)
      form.reset()
    } catch {
      toast.error('Failed to send message. Please try again.')
    }
  }

  if (submitted) {
    return (
      <motion.div
        className="border border-border bg-card/50 p-8 md:p-10"
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <p className="font-serif text-2xl md:text-3xl tracking-tight text-foreground mb-3">
          Message sent
        </p>
        <p className="text-muted-foreground leading-relaxed">
          Thank you - we typically reply within one business day.
        </p>
      </motion.div>
    )
  }

  return (
    <div className="border border-border bg-card/30 p-8 md:p-10">
      <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-foreground mb-8">
        Send us a message
      </h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="relative space-y-6">
          <FormField
            control={form.control}
            name="hpField"
            render={({ field }) => (
              <FormItem
                className="absolute -left-[9999px] top-0 m-0 h-px w-px overflow-hidden p-0 [clip:rect(0,0,0,0)]"
                aria-hidden="true"
              >
                <FormControl>
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    className="h-px w-px border-0 p-0 opacity-0"
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={labelClass}>Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Your name" className={fieldClass} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClass}>Email *</FormLabel>
                  <FormControl>
                    <Input placeholder="you@company.com" className={fieldClass} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClass}>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="(555) 123-4567" className={fieldClass} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={labelClass}>Message *</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Tell us about your project..."
                    rows={6}
                    className={cn(fieldClass, 'min-h-[9rem] resize-y')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="pt-2">
            <button
              type="submit"
              disabled={form.formState.isSubmitting}
              className={cn(
                siteButtonClass.primary,
                'disabled:pointer-events-none disabled:opacity-50'
              )}
            >
              {form.formState.isSubmitting ? 'Sending...' : 'Send message'}
            </button>
          </div>
        </form>
      </Form>
    </div>
  )
}

export default function ContactPage() {
  const reduceMotion = useReducedMotion()

  return (
    <SiteShell>
      <div className="w-full bg-background pt-24 pb-20 md:pb-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 md:mb-24 max-w-3xl">
            <RevealText>
              <span className="text-xs tracking-[0.3em] uppercase text-accent">Contact</span>
            </RevealText>
            <RevealText delay={0.1}>
              <h1 className="mt-4 text-4xl md:text-6xl lg:text-7xl font-serif tracking-tight text-foreground">
                Let&apos;s talk about
                <br />
                <span className="italic text-muted-foreground">what you&apos;re building</span>
              </h1>
            </RevealText>
            <RevealText delay={0.2}>
              <p className="mt-6 text-xl text-muted-foreground font-serif italic leading-relaxed max-w-2xl">
                Have a project in mind? Tell us what you need - we typically reply within one
                business day.
              </p>
            </RevealText>
          </div>

          <motion.div
            className="grid md:grid-cols-12 gap-12 lg:gap-20 items-start"
            initial={reduceMotion ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="md:col-span-5 space-y-10">
              {contactDetails.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={reduceMotion ? false : { opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: reduceMotion ? 0 : 0.5,
                    delay: reduceMotion ? 0 : 0.1 + i * 0.06,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  <span className={`${labelClass} block mb-2`}>{item.label}</span>
                  {'href' in item && item.href ? (
                    <a
                      href={item.href}
                      target={'external' in item && item.external ? '_blank' : undefined}
                      rel={
                        'external' in item && item.external ? 'noreferrer' : undefined
                      }
                      className="text-xl font-serif text-foreground hover:text-accent transition-colors"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-xl font-serif text-foreground">{item.value}</p>
                  )}
                </motion.div>
              ))}
            </div>

            <motion.div
              className="md:col-span-7"
              initial={reduceMotion ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: reduceMotion ? 0 : 0.5,
                delay: reduceMotion ? 0 : 0.2,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <ContactForm />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </SiteShell>
  )
}
